import React, { ReactNode } from 'react';
import ButtonWithProgress from 'src/scripts/templates/_shared/ui-components/YogaUI/ButtonWithProgress';
import { REVIEW_SORT_OPTIONS, REVIEW_SORT_OPTIONS_ARRAY } from 'src/scripts/utils/reviews';
import { useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { getSortStyle } from 'src/scripts/templates/product-listing-page/components/sorting-utils';
import sortDropdownStyles from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloDropdown/sorting.module.scss';
import styles from './paginatedReviewsStyles.module.scss';
import AloDropdown from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloDropdown';
import { ReviewSort } from '../../../types/reviews';
import { pluralize } from 'src/scripts/utils/helpers';

const PaginatedReviews = ({
  children,
  productReviewsCount,
  productReviewsTotalCount,
  handleLoadMore,
  loading,
  sortValue,
  setSortValue,
}: {
  children: ReactNode;
  productReviewsCount: number;
  productReviewsTotalCount: number;
  handleLoadMore?: () => void;
  loading: boolean;
  sortValue: ReviewSort;
  setSortValue: (value: ReviewSort) => void;
}) => {
  return (
    <div className={styles.paginatedReviewsContainer}>
      <PaginatedReviewHeader
        productReviewsCount={productReviewsCount}
        productReviewsTotalCount={productReviewsTotalCount}
        sortValue={sortValue}
        setSortValue={setSortValue}
      />
      {children}
      <PaginatedReviewFooter
        productReviewsCount={productReviewsCount}
        productReviewsTotalCount={productReviewsTotalCount}
        handleLoadMore={handleLoadMore}
        loading={loading}
      />
    </div>
  );
};

const PaginatedReviewHeader = ({
  productReviewsCount,
  productReviewsTotalCount,
  sortValue,
  setSortValue,
}: {
  productReviewsCount: number;
  productReviewsTotalCount: number;
  sortValue: ReviewSort;
  setSortValue: (value: ReviewSort) => void;
}) => {
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT);
  const sortInput = { key: sortValue, text: REVIEW_SORT_OPTIONS[sortValue] };

  return (
    <div className={styles.paginationHeader}>
      <div className={styles.headerLeft}>
        <p className="body">
          {productReviewsCount > 0
            ? `1-${productReviewsCount} of
            ${pluralize(productReviewsTotalCount, 'Review')}`
            : `0 Reviews`}
        </p>
      </div>
      {isMobile ? null : (
        <div className={styles.headerRight}>
          {/* Using AloDropdown for sorting reviews as we no need to set the search params here */}
          <AloDropdown
            onChange={({ key }) => {
              // key always matches the ReviewSort type so we can safely cast it
              setSortValue(key as ReviewSort);
            }}
            placeholder="SORT"
            value={sortInput}
            dropdownItems={REVIEW_SORT_OPTIONS_ARRAY.map((sortOption) => ({
              key: sortOption.value,
              text: sortOption.label,
            }))}
            MUIAutocompleteProps={{
              closeIcon: false,
              blurOnSelect: true,
              selectOnFocus: false,
              openOnFocus: true,
              getOptionSelected: (option, value) => option.key === value.key,
              classes: getSortStyle(sortInput),
            }}
            // reusing the same styles(sortDropdownStyles) for the reviews sorting dropdown, not required duplication.
            wrapperClass={`${sortDropdownStyles.sorting} ${styles.pdpReviewsSorting}`}
            searchable
            inputReadOnly={true}
            labelPrefix={'Sort by'}
            hasBorder={false}
          />
        </div>
      )}
    </div>
  );
};

const PaginatedReviewFooter = ({
  productReviewsCount,
  productReviewsTotalCount,
  handleLoadMore,
  loading,
}: {
  productReviewsCount: number;
  productReviewsTotalCount: number;
  handleLoadMore?: () => void;
  loading: boolean;
}) => {
  const remainingReviews = productReviewsTotalCount - productReviewsCount;

  if (productReviewsTotalCount > 0) {
    return (
      <div className={styles.paginationFooter}>
        {productReviewsCount >= productReviewsTotalCount ? null : (
          <ButtonWithProgress
            options={{
              wrapperClasses: `button-wrapper ${styles.loadMoreButtonWrapper}`,
              showProgressAnimation: loading,
              buttonTypeClass: 'button-ghost',
              buttonClasses: `${styles.loadMoreButton} notranslate`,
              cta: `Load ${remainingReviews >= 10 ? `10` : remainingReviews} More`,
              onClick: () => {
                if (handleLoadMore) handleLoadMore();
              },
              disabled: loading,
            }}
          />
        )}
        {productReviewsTotalCount > 0 && (
          <p className={`body notranslate ${styles.paginationFooterText}`}>
            Viewing <span className="body semibold notranslate">1 - {productReviewsCount}</span> of{' '}
            <span className="body semibold">{productReviewsTotalCount}</span>
          </p>
        )}
      </div>
    );
  } else return null;
};

export default PaginatedReviews;
